import {
  ApiBlueprint,
  createApiFactory,
  discoveryApiRef,
  fetchApiRef,
  createFrontendModule,
} from '@backstage/frontend-plugin-api';
import { sonarQubeApiRef } from '@backstage-community/plugin-sonarqube-react';
import { SonarQubeClient } from '@backstage-community/plugin-sonarqube';

/**
 * API factory for the legacy (old frontend system) SonarQube plugin whose React
 * component is rendered by the entity page overview card.
 *
 * In the old app this api factory was contributed automatically by the plugin's
 * `createPlugin({ apis: [...] })`. In the new frontend system the plugin is not
 * registered as a feature, so its apiRef would be missing - causing the
 * rendered SonarQube overview card to throw `NotImplementedError`. Registering
 * the factory here makes the card functional (it calls the existing proxy
 * endpoint).
 *
 * The Snyk api factory is NOT registered here: the Snyk plugin is registered as
 * a feature in `App.tsx` via `convertLegacyPlugin` (needed for its routable
 * entity-tab content), which already contributes `snykApiRef`.
 *
 * Factory definition mirrors the plugin's own `createApiFactory(...)`.
 */

/** Builds the SonarQube API client from its dependencies (exported for testing). */
export const createSonarQubeApi = (deps: {
  discoveryApi: any;
  fetchApi: any;
}) => new SonarQubeClient(deps);

const sonarQubeApiExtension = ApiBlueprint.make({
  name: 'sonarqube',
  params: defineParams =>
    defineParams(
      createApiFactory({
        api: sonarQubeApiRef,
        deps: { discoveryApi: discoveryApiRef, fetchApi: fetchApiRef },
        factory: createSonarQubeApi,
      }),
    ),
});

/**
 * Module contributing the legacy plugin API factory consumed by the entity
 * page card. Targets `pluginId: 'app'` so the api is app-scoped.
 */
export const pluginApisModule = createFrontendModule({
  pluginId: 'app',
  extensions: [sonarQubeApiExtension],
});